import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { loadCareer } from '../utils/contentLoader';

const TYPE_META = {
  work:           { label: 'Work',            color: '#7cb8a0' },
  education:      { label: 'Education',       color: '#8a9fd4' },
  extracurricular:{ label: 'Extracurricular', color: '#c4a24a' },
};

const FILTERS = ['all', 'work', 'education', 'extracurricular'];

export default function Career() {
  const [activeFilter, setActiveFilter] = useState('all');
  const allEntries = useMemo(() => loadCareer(), []);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const filtered = useMemo(() => {
    if (activeFilter === 'all') return allEntries;
    return allEntries.filter((e) => e.type === activeFilter);
  }, [allEntries, activeFilter]);

  return (
    <div style={{ padding: '48px', maxWidth: '860px' }}>
      <PageHeader
        eyebrow="05 — Career & Education"
        title="Career & Ed"
        subtitle="The road so far — from math competitions at age eleven to Enterprise AI."
      />

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '48px' }}>
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setActiveFilter(f)}
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.65rem',
              letterSpacing: '0.06em',
              padding: '5px 12px',
              borderRadius: '4px',
              border: '1px solid',
              cursor: 'pointer',
              background: activeFilter === f ? 'var(--gold)' : 'transparent',
              borderColor: activeFilter === f ? 'var(--gold)' : 'var(--border)',
              color: activeFilter === f ? '#0d0c10' : 'var(--text-3)',
              transition: 'all 0.15s ease',
              textTransform: 'uppercase',
            }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div style={{ position: 'relative' }}>
        {/* Vertical line */}
        <div style={{
          position: 'absolute',
          left: isMobile ? '10px' : '50%',
          top: 0,
          bottom: 0,
          width: '1px',
          background: 'var(--border)',
          transform: isMobile ? 'none' : 'translateX(-50%)',
        }} />

        {filtered.map((entry, i) => {
          const meta = TYPE_META[entry.type] || TYPE_META.work;
          const isLeft = i % 2 === 0;

          return (
            <div
              key={entry.slug}
              className="timeline-entry"
              style={{
                display: 'flex',
                justifyContent: isMobile ? 'flex-end' : (isLeft ? 'flex-start' : 'flex-end'),
                position: 'relative',
                marginBottom: '32px',
              }}
            >
              {/* Card */}
              <Link
                to={`/career/${entry.slug}`}
                style={{
                  textDecoration: 'none',
                  width: isMobile ? 'calc(100% - 28px)' : '46%',
                }}
              >
                <div
                  className="card-hover"
                  style={{
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '20px',
                    borderTop: `2px solid ${meta.color}`,
                  }}
                >
                  {/* Date range */}
                  <div style={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.58rem',
                    color: 'var(--text-3)',
                    letterSpacing: '0.05em',
                    marginBottom: '6px',
                  }}>
                    {entry.dateRange}
                  </div>

                  {/* Role */}
                  <h3 style={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontSize: '1.15rem',
                    fontWeight: 600,
                    color: 'var(--text)',
                    margin: '0 0 4px',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                  }}>
                    {entry.role}
                  </h3>

                  {/* Institution */}
                  <div style={{
                    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                    fontSize: '0.78rem',
                    color: 'var(--text-2)',
                    marginBottom: '4px',
                  }}>
                    {entry.institution}
                  </div>

                  {/* Location */}
                  {entry.location && (
                    <div style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.58rem',
                      color: 'var(--text-3)',
                    }}>
                      {entry.location}
                    </div>
                  )}

                  {/* Current badge */}
                  {entry.current && (
                    <div style={{
                      marginTop: '10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#4ade80',
                        boxShadow: '0 0 0 2px rgba(74,222,128,0.2)',
                      }} />
                      <span style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.55rem',
                        color: '#4ade80',
                        letterSpacing: '0.05em',
                      }}>
                        CURRENT
                      </span>
                    </div>
                  )}

                  {/* Skills chips */}
                  {entry.skills && entry.skills.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '12px' }}>
                      {entry.skills.slice(0, 4).map((s) => (
                        <span key={s} className="tag">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>

              {/* Timeline dot */}
              <div
                className={`timeline-dot${entry.current ? ' current' : ''}`}
                style={{
                  position: 'absolute',
                  left: isMobile ? '10px' : '50%',
                  top: '22px',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 2,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
